import React, { useRef, useState, useEffect, useCallback } from "react";

import formatBoldIcon from "../../../../../../assets/svg/Format_bold.svg";
import formatItalicIcon from "../../../../../../assets/svg/Format_italic.svg";
import formatStrikeThroughIcon from "../../../../../../assets/svg/Strikethrough.svg";

import {
  CharCounter,
  ChipTooltip,
  CreateVariableButton,
  EditorArea,
  EditorContainer,
  ErrorMessage,
  Toolbar,
  ToolbarButton,
  ToolbarDivider,
} from "./WhatsAppBodyEditor.style";

// ─── Constants ────────────────────────────────────────────────────────────────

const BLOCK_TAGS = new Set([
  "p",
  "div",
  "li",
  "tr",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "blockquote",
  "pre",
]);

// ─── DOM Walker Helpers ───────────────────────────────────────────────────────

/**
 * Walk the DOM tree and extract WhatsApp markdown text.
 * Returns { markdown: string, variables: string[] }
 */
function extractMarkdownFromDom(rootNode) {
  const processedVars = new Map(); // varName → 1-based index
  const variablesList = []; // ordered unique variable names

  const convertInline = (node) => {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent;
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName.toLowerCase();

    if (tag === "br") return "\n";

    if (tag === "span" && node.classList.contains("variable-chip")) {
      const varName =
        node.getAttribute("data-variable-name") || node.textContent.trim();
      if (!processedVars.has(varName)) {
        processedVars.set(varName, processedVars.size + 1);
        variablesList.push(varName);
      }
      return `{{${processedVars.get(varName)}}}`;
    }

    const children = Array.from(node.childNodes).map(convertInline).join("");

    if (tag === "b" || tag === "strong") return `*${children}*`;
    if (tag === "i" || tag === "em") return `_${children}_`;
    if (tag === "s" || tag === "del" || tag === "strike")
      return `~${children}~`;
    if (tag === "code") return `\`\`\`${children}\`\`\``;

    return children;
  };

  // Check if a node is a block-level element
  const isBlockNode = (node) =>
    node.nodeType === Node.ELEMENT_NODE &&
    BLOCK_TAGS.has(node.tagName.toLowerCase());

  // Group top-level children: consecutive inline nodes merge into one block,
  // block-level nodes become their own block.
  const markdownBlocks = [];
  let inlineBuf = "";

  const flushInlineBuf = () => {
    if (inlineBuf) {
      markdownBlocks.push(inlineBuf);
      inlineBuf = "";
    }
  };

  Array.from(rootNode.childNodes).forEach((node) => {
    if (isBlockNode(node)) {
      flushInlineBuf();
      let inner = Array.from(node.childNodes).map(convertInline).join("");
      // Strip exactly one trailing \n (browser cursor anchor)
      inner = inner.replace(/\n$/, "");
      markdownBlocks.push(inner);
    } else {
      inlineBuf += convertInline(node);
    }
  });
  flushInlineBuf();

  // Flatten blocks — each paragraph becomes a line, blocks separated by blank line
  const allLines = [];
  for (const block of markdownBlocks) {
    const sublines = block.split("\n");
    for (const line of sublines) {
      allLines.push(line);
    }
    allLines.push(""); // paragraph separator
  }

  // Remove the last trailing separator
  if (allLines.length > 0 && allLines[allLines.length - 1] === "") {
    allLines.pop();
  }

  // Strip leading/trailing empty lines
  while (allLines.length && allLines[0] === "") allLines.shift();
  while (allLines.length && allLines[allLines.length - 1] === "")
    allLines.pop();

  // Collapse 3+ consecutive empty lines into max 1 blank line
  const collapsed = allLines.reduce((acc, l) => {
    if (l === "" && acc.length > 0 && acc[acc.length - 1] === "") return acc;
    acc.push(l);
    return acc;
  }, []);

  const markdown = collapsed.join("\n").trim();
  return { markdown, variables: variablesList };
}

/**
 * Walk the DOM tree and extract clean HTML for preview.
 * Returns { html: string }
 */
function extractHtmlFromDom(rootNode) {
  const convertInline = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      return escapeHtml(node.textContent);
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return "";

    const tag = node.tagName.toLowerCase();

    if (tag === "br") return "<br>";

    if (tag === "span" && node.classList.contains("variable-chip")) {
      const varName =
        node.getAttribute("data-variable-name") || node.textContent.trim();
      return `<span class="variable-chip" contenteditable="false">${escapeHtml(varName)}</span>`;
    }

    const children = Array.from(node.childNodes).map(convertInline).join("");

    if (tag === "b" || tag === "strong") return `<b>${children}</b>`;
    if (tag === "i" || tag === "em") return `<i>${children}</i>`;
    if (tag === "s" || tag === "del" || tag === "strike")
      return `<s>${children}</s>`;
    if (tag === "code") return `<code>${children}</code>`;

    return children;
  };

  // Check if a node is a block-level element
  const isBlockNode = (node) =>
    node.nodeType === Node.ELEMENT_NODE &&
    BLOCK_TAGS.has(node.tagName.toLowerCase());

  // Group top-level children: consecutive inline nodes merge into one block,
  // block-level nodes become their own block.
  const blocks = [];
  let inlineBuffer = "";

  const flushInline = () => {
    if (inlineBuffer) {
      blocks.push(inlineBuffer);
      inlineBuffer = "";
    }
  };

  Array.from(rootNode.childNodes).forEach((node) => {
    if (isBlockNode(node)) {
      flushInline();
      let inner = Array.from(node.childNodes).map(convertInline).join("");
      // Strip exactly one trailing <br> (browser cursor anchor)
      inner = inner.replace(/<br>$/, "");
      blocks.push(inner);
    } else {
      // Inline node (text, <b>, <i>, <s>, <code>, <span>, etc.)
      inlineBuffer += convertInline(node);
    }
  });
  flushInline();

  // Expand blocks that contain <br> into separate <p> elements
  // so that line breaks inside a single block become real paragraphs
  const expanded = [];
  for (const b of blocks) {
    if (b.includes("<br>")) {
      const parts = b.split("<br>");
      for (const part of parts) {
        expanded.push(part);
      }
    } else {
      expanded.push(b);
    }
  }

  // Strip leading/trailing empty blocks
  while (expanded.length && expanded[0] === "") expanded.shift();
  while (expanded.length && expanded[expanded.length - 1] === "")
    expanded.pop();

  // Collapse 3+ consecutive empty blocks to max 1
  const collapsed = expanded.reduce((acc, b) => {
    if (b === "" && acc.length > 0 && acc[acc.length - 1] === "") return acc;
    acc.push(b);
    return acc;
  }, []);

  const html = collapsed
    .map((b) => (b ? `<p>${b}</p>` : "<p><br></p>"))
    .join("");

  return { html };
}

// ─── Utility ──────────────────────────────────────────────────────────────────

function escapeHtml(text) {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function getTextLength(rootNode) {
  let length = 0;
  const walk = (node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      length += node.textContent.length;
    } else if (
      node.nodeType === Node.ELEMENT_NODE &&
      node.tagName.toLowerCase() === "span" &&
      node.classList.contains("variable-chip")
    ) {
      const varName =
        node.getAttribute("data-variable-name") || node.textContent.trim();
      length += varName.length;
    } else {
      Array.from(node.childNodes).forEach(walk);
    }
  };
  walk(rootNode);
  return length;
}

/**
 * Check if the given selection is (at least partially) inside targetNode.
 */
function isSelectionInside(selection, targetNode) {
  if (!selection || selection.rangeCount === 0) return false;
  const range = selection.getRangeAt(0);
  return (
    targetNode.contains(range.startContainer) &&
    targetNode.contains(range.endContainer)
  );
}

/**
 * Get the nearest ancestor matching the tag from a node, stopping at boundary.
 */
function getAncestorWithTag(node, tagName, boundary) {
  let current = node;
  while (current && current !== boundary) {
    if (
      current.nodeType === Node.ELEMENT_NODE &&
      current.tagName.toLowerCase() === tagName
    ) {
      return current;
    }
    current = current.parentNode;
  }
  return null;
}

/**
 * Unwrap a node: replace it with its children in the DOM.
 */
function unwrapNode(node) {
  const parent = node.parentNode;
  if (!parent) return;
  while (node.firstChild) {
    parent.insertBefore(node.firstChild, node);
  }
  parent.removeChild(node);
}

/**
 * Wrap the current Range selection in the given tag element.
 * If the selection is already fully inside that tag, unwrap instead.
 */
function toggleFormatOnSelection(tagName, editorRef) {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) return;
  if (!isSelectionInside(selection, editorRef.current)) return;

  const range = selection.getRangeAt(0);

  // Check if entire selection is inside an existing tag of this type
  const ancestorTag = getAncestorWithTag(
    range.commonAncestorContainer,
    tagName,
    editorRef.current,
  );

  if (ancestorTag) {
    // Unwrap: remove the format tag, keep its content
    unwrapNode(ancestorTag);
  } else {
    // Wrap: extract the range contents into a new element
    if (range.collapsed) return; // nothing selected, nothing to wrap

    const wrapper = document.createElement(tagName);
    try {
      range.surroundContents(wrapper);
    } catch (_e) {
      // surroundContents throws if selection crosses element boundaries
      // Fall back to extractContents + insertNode
      const fragment = range.extractContents();
      wrapper.appendChild(fragment);
      range.insertNode(wrapper);
    }

    // Restore selection inside the wrapper
    const newRange = document.createRange();
    newRange.selectNodeContents(wrapper);
    selection.removeAllRanges();
    selection.addRange(newRange);
  }
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * WhatsAppBodyEditor — standalone rich-text editor for WhatsApp message bodies.
 *
 * Props:
 *   initialHtml        {string}   HTML to restore (from broadcastData.bodyMessageWithFormat)
 *   initialVariables   {string[]} Ordered variable names matching {{N}} placeholders
 *   onChangeMarkdown   {(markdown: string) => void}
 *   onChangeHtml       {(html: string) => void}
 *   onChangeVariables  {(variables: string[]) => void}
 *   maxLength          {number}   Default 1024
 *   error              {boolean}  External error state
 *   errorMessage       {string}   Error text to display
 */
function WhatsAppBodyEditor({
  initialHtml,
  initialVariables,
  onChangeMarkdown,
  onChangeHtml,
  onChangeVariables,
  maxLength = 1024,
  error = false,
  errorMessage,
}) {
  const editorRef = useRef(null);
  const initializedRef = useRef(false);

  const [showCreateVariable, setShowCreateVariable] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const [internalError, setInternalError] = useState(false);
  const [activeFormats, setActiveFormats] = useState({
    b: false,
    i: false,
    s: false,
    code: false,
  });
  const [chipTooltip, setChipTooltip] = useState(null);

  // ── Extract & emit ──────────────────────────────────────────────────────────

  const extractAndEmit = useCallback(() => {
    const editor = editorRef.current;
    if (!editor) return;

    // Character count (text only, no HTML markup)
    const textLen = getTextLength(editor);
    setCharCount(textLen);
    const overLimit = textLen > maxLength;
    setInternalError(overLimit);

    const { markdown, variables } = extractMarkdownFromDom(editor);
    const { html } = extractHtmlFromDom(editor);

    if (onChangeMarkdown) onChangeMarkdown(overLimit ? "" : markdown);
    if (onChangeHtml) onChangeHtml(overLimit ? "" : html);
    if (onChangeVariables) onChangeVariables(variables);
  }, [maxLength, onChangeMarkdown, onChangeHtml, onChangeVariables]);

  // ── Initialization ──────────────────────────────────────────────────────────

  useEffect(() => {
    if (initializedRef.current) return;
    if (!editorRef.current) return;

    if (initialHtml && initialHtml.trim()) {
      let html = initialHtml;

      // Restore variable chips if we have the variable list
      if (initialVariables && initialVariables.length > 0) {
        html = html.replace(/\{\{(\d+)\}\}/g, (match, num) => {
          const idx = parseInt(num, 10) - 1;
          if (idx >= 0 && idx < initialVariables.length) {
            const varName = initialVariables[idx];
            return `<span class="variable-chip" contenteditable="false" data-variable-name="${varName}">${varName}</span>`;
          }
          return match;
        });
      }

      editorRef.current.innerHTML = html;
      extractAndEmit();
    }

    initializedRef.current = true;
  }, [initialHtml, initialVariables, extractAndEmit]);

  // ── Selection tracking ──────────────────────────────────────────────────────

  const handleSelectionChange = useCallback(() => {
    const selection = window.getSelection();
    const editor = editorRef.current;

    if (
      selection &&
      !selection.isCollapsed &&
      editor &&
      isSelectionInside(selection, editor)
    ) {
      setShowCreateVariable(true);
    } else {
      setShowCreateVariable(false);
    }

    // Detect active formats at cursor position
    if (selection && selection.rangeCount > 0 && editor) {
      const node = selection.focusNode;
      setActiveFormats({
        b: !!getAncestorWithTag(node, "b", editor) || !!getAncestorWithTag(node, "strong", editor),
        i: !!getAncestorWithTag(node, "i", editor) || !!getAncestorWithTag(node, "em", editor),
        s: !!getAncestorWithTag(node, "s", editor) || !!getAncestorWithTag(node, "del", editor) || !!getAncestorWithTag(node, "strike", editor),
        code: !!getAncestorWithTag(node, "code", editor),
      });
    } else {
      setActiveFormats({ b: false, i: false, s: false, code: false });
    }
  }, []);

  // ── Format toggle ───────────────────────────────────────────────────────────

  const handleToggleFormat = useCallback(
    (tagName) => {
      const selection = window.getSelection();
      if (!selection || !editorRef.current) return;
      if (!isSelectionInside(selection, editorRef.current)) {
        editorRef.current.focus();
        return;
      }

      toggleFormatOnSelection(tagName, editorRef);
      editorRef.current.focus();
      extractAndEmit();
      handleSelectionChange();
    },
    [extractAndEmit, handleSelectionChange],
  );

  // ── Variable creation ───────────────────────────────────────────────────────

  const handleCreateVariable = useCallback(() => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    if (!isSelectionInside(selection, editorRef.current)) return;

    const range = selection.getRangeAt(0);
    if (range.collapsed) return;

    const selectedText = range.toString().trim();
    if (!selectedText) return;

    // Delete selected content and insert a chip
    range.deleteContents();

    const chip = document.createElement("span");
    chip.className = "variable-chip";
    chip.setAttribute("contenteditable", "false");
    chip.setAttribute("data-variable-name", selectedText);
    chip.textContent = selectedText;
    range.insertNode(chip);

    // Move cursor after the chip
    const afterRange = document.createRange();
    afterRange.setStartAfter(chip);
    afterRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(afterRange);

    setShowCreateVariable(false);
    extractAndEmit();
  }, [extractAndEmit]);

  // ── Variable deletion (click on chip) ──────────────────────────────────────

  const handleEditorClick = useCallback(
    (e) => {
      const target = e.target;
      if (
        target.nodeType === Node.ELEMENT_NODE &&
        target.classList.contains("variable-chip")
      ) {
        setChipTooltip(null);
        const varName =
          target.getAttribute("data-variable-name") ||
          target.textContent.trim();
        const textNode = document.createTextNode(varName);
        target.parentNode.replaceChild(textNode, target);
        extractAndEmit();
      }
    },
    [extractAndEmit],
  );

  // ── Chip tooltip (mouseover delegation) ────────────────────────────────────

  const handleEditorMouseOver = useCallback((e) => {
    const target = e.target;
    if (
      target.nodeType === Node.ELEMENT_NODE &&
      target.classList.contains("variable-chip")
    ) {
      const rect = target.getBoundingClientRect();
      setChipTooltip({
        top: rect.top - 8,
        left: rect.left + rect.width / 2,
      });
    }
  }, []);

  const handleEditorMouseOut = useCallback((e) => {
    const target = e.target;
    if (
      target.nodeType === Node.ELEMENT_NODE &&
      target.classList.contains("variable-chip")
    ) {
      setChipTooltip(null);
    }
  }, []);

  // ── Paste handling ──────────────────────────────────────────────────────────

  const handlePaste = useCallback(
    (e) => {
      e.preventDefault();

      const html = e.clipboardData.getData("text/html");
      const plain = e.clipboardData.getData("text/plain");

      let insertHtml;

      if (html) {
        const doc = new DOMParser().parseFromString(html, "text/html");

        const convertInline = (node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            return escapeHtml(node.textContent);
          }
          if (node.nodeType !== Node.ELEMENT_NODE) return "";

          const tag = node.tagName.toLowerCase();
          const children = Array.from(node.childNodes)
            .map(convertInline)
            .join("");

          if (tag === "b" || tag === "strong") return `<b>${children}</b>`;
          if (tag === "i" || tag === "em") return `<i>${children}</i>`;
          if (tag === "s" || tag === "del" || tag === "strike")
            return `<s>${children}</s>`;
          if (tag === "code") return `<code>${children}</code>`;
          if (tag === "br") return "";
          return children; // strip unknown tags, keep text
        };

        const collectBlocks = (node) => {
          if (node.nodeType === Node.TEXT_NODE) {
            const text = node.textContent.trim();
            return text ? [escapeHtml(text)] : [];
          }
          if (node.nodeType !== Node.ELEMENT_NODE) return [];

          const tag = node.tagName.toLowerCase();
          if (BLOCK_TAGS.has(tag)) {
            const inner = Array.from(node.childNodes)
              .map(convertInline)
              .join("")
              .trim();
            return [inner];
          }
          return [convertInline(node)];
        };

        let blocks = Array.from(doc.body.childNodes).flatMap(collectBlocks);

        // Strip leading/trailing empty blocks
        while (blocks.length && blocks[0] === "") blocks.shift();
        while (blocks.length && blocks[blocks.length - 1] === "") blocks.pop();

        // Collapse consecutive empty blocks
        blocks = blocks.reduce((acc, b) => {
          if (b === "" && acc.length > 0 && acc[acc.length - 1] === "")
            return acc;
          acc.push(b);
          return acc;
        }, []);

        insertHtml = blocks
          .map((b) => (b ? `<p>${b}</p>` : "<p><br></p>"))
          .join("");
      } else {
        // Plain text fallback
        insertHtml = plain
          .split("\n")
          .map((line) => line.trim())
          .filter((line) => line.length > 0)
          .map((line) => `<p>${escapeHtml(line)}</p>`)
          .join("");
      }

      if (insertHtml) {
        // execCommand('insertHTML') is the one acceptable use — it maintains undo history
        document.execCommand("insertHTML", false, insertHtml);
      }

      extractAndEmit();
    },
    [extractAndEmit],
  );

  // ── Input handler ───────────────────────────────────────────────────────────

  const handleInput = useCallback(() => {
    extractAndEmit();
  }, [extractAndEmit]);

  // ── Computed state ──────────────────────────────────────────────────────────

  const showError = error || internalError;
  const effectiveErrorMessage =
    errorMessage ||
    (internalError
      ? `El mensaje no puede superar ${maxLength} caracteres.`
      : null);

  return (
    <EditorContainer>
      <Toolbar>
        <ToolbarButton
          type="button"
          title="Negrita (*texto*)"
          $active={activeFormats.b}
          onClick={() => handleToggleFormat("b")}
        >
          <img src={formatBoldIcon} alt="Negrita" />
        </ToolbarButton>

        <ToolbarButton
          type="button"
          title="Cursiva (_texto_)"
          $active={activeFormats.i}
          onClick={() => handleToggleFormat("i")}
        >
          <img src={formatItalicIcon} alt="Cursiva" />
        </ToolbarButton>

        <ToolbarButton
          type="button"
          title="Tachado (~texto~)"
          $active={activeFormats.s}
          onClick={() => handleToggleFormat("s")}
        >
          <img src={formatStrikeThroughIcon} alt="Tachado" />
        </ToolbarButton>

        <ToolbarButton
          type="button"
          title="Monoespaciado (```texto```)"
          $active={activeFormats.code}
          onClick={() => handleToggleFormat("code")}
          style={{ fontFamily: "monospace", fontSize: "0.9rem" }}
        >
          {"{ }"}
        </ToolbarButton>

        {showCreateVariable && (
          <>
            <ToolbarDivider />
            <CreateVariableButton type="button" onClick={handleCreateVariable}>
              Crear campo
            </CreateVariableButton>
          </>
        )}
      </Toolbar>

      <EditorArea
        contentEditable
        ref={editorRef}
        $error={showError}
        data-placeholder="Escribe el mensaje aquí..."
        onMouseUp={handleSelectionChange}
        onKeyUp={handleSelectionChange}
        onInput={handleInput}
        onPaste={handlePaste}
        onClick={handleEditorClick}
        onMouseOver={handleEditorMouseOver}
        onMouseOut={handleEditorMouseOut}
        suppressContentEditableWarning
      />

      {chipTooltip && (
        <ChipTooltip $top={chipTooltip.top} $left={chipTooltip.left}>
          Click para convertir a texto
        </ChipTooltip>
      )}

      <CharCounter $overLimit={charCount > maxLength}>
        {charCount} / {maxLength}
      </CharCounter>

      {showError && effectiveErrorMessage && (
        <ErrorMessage>{effectiveErrorMessage}</ErrorMessage>
      )}
    </EditorContainer>
  );
}

export default WhatsAppBodyEditor;
